import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Snackbar, Typography } from '@mui/material';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { useTheme, alpha } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { getReadings, calculateStage } from '../../../services/directShearReadingService';
import type { ReadingsByStage } from '../../../types/directShearReading';
import type { AnalysisData, AnalysisRanges } from './analysisTypes';
import { EMPTY_ANALYSIS } from './analysisTypes';
import { getAnalysis, saveAnalysisRanges } from './analysisService';
import type { DirectShearStage } from '../../../types/directShear';
import { StageAnalysis } from './StageAnalysis';
import { SummaryAnalysis } from './SummaryAnalysis';
import { usePermissions } from '../../../hooks/usePermissions';

/** Spec default for the dilation averaging window (mm) — ResultEntry-Calculations-Spec §DilationRange. */
const DEFAULT_DILATION_RANGE = '0.375';

interface AnalysisPlotsPageProps {
  generalKey: string;
  stages?: DirectShearStage[];
  /** Test-level DirectShearGeneral.DilationRange — the fallback for stages that have never been calculated. */
  savedDilationRange?: number | null;
  onRecalculated?: () => void;
}

// BUG-2026-0199 #2: previously (REQ-01, 2026-06-12) the Peak range was
// force-defaulted to 0–1 mm on every load/recalc so operators didn't re-enter
// it. The lab now needs to be able to LEAVE the Peak boxes blank so that no
// peak is selected for a stage. The auto-default made that impossible (a
// cleared value was silently refilled to 0–1 on the next load/recalc), so it
// has been removed — the boxes now reflect exactly what is stored, and a blank
// Peak range means "no peak" (the backend already persists null and clears the
// computed peak accordingly).

export const AnalysisPlotsPage = ({ generalKey, stages, savedDilationRange, onRecalculated }: AnalysisPlotsPageProps) => {
  const theme = useTheme();
  const { canEditTestData } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [readingsByStage, setReadingsByStage] = useState<ReadingsByStage>({});
  const [analysisByStage, setAnalysisByStage] = useState<Record<number, AnalysisData>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeStageTab, setActiveStageTabState] = useState(() => {
    const s = parseInt(searchParams.get('stage') || '0', 10);
    return s >= 0 ? s : 0;
  });

  const setActiveStageTab = useCallback((tab: number) => {
    setActiveStageTabState(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 0) next.delete('stage');
      else next.set('stage', String(tab));
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean; message: string; severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Dilation Range — PER STAGE (BUG-2026-0211). Each stage's stored window
  // arrives on its analysis payload (DirectShearDetail.DilationRange, mig 130);
  // `dilationDraft` holds what the user is editing for a stage before pressing
  // Apply. A stage that has never been calculated falls back to the test-level
  // value (DirectShearGeneral.DilationRange), then the 0.375 mm spec default.
  const [dilationDraft, setDilationDraft] = useState<Record<number, string>>({});
  const fallbackDilation = savedDilationRange != null ? String(savedDilationRange) : DEFAULT_DILATION_RANGE;
  const dilationFor = useCallback((stageNumber: number): string => {
    const draft = dilationDraft[stageNumber];
    if (draft != null) return draft;
    const stored = analysisByStage[stageNumber]?.dilationRange;
    return stored != null ? String(stored) : fallbackDilation;
  }, [dilationDraft, analysisByStage, fallbackDilation]);
  const setDilationFor = useCallback((stageNumber: number, value: string) => {
    setDilationDraft((prev) => ({ ...prev, [stageNumber]: value }));
  }, []);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcProgress, setRecalcProgress] = useState('');
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const loadDataRef = useRef<(() => Promise<void>) | undefined>(undefined);

  // "Apply" recalculates ONLY the stage whose slider was changed, with that
  // stage's own window — other stages keep the value they were last computed
  // with (BUG-2026-0211; previously one shared value was pushed to every stage).
  const doRecalculate = useCallback(async (stageNumber: number, confirmReset = false) => {
    if (!generalKey || !canEditTestData) return;
    const dr = parseFloat(dilationFor(stageNumber));
    if (isNaN(dr) || dr <= 0) {
      setSnackbar({ open: true, message: 'Dilation Range must be a positive number', severity: 'error' });
      return;
    }
    setRecalculating(true);
    setRecalcProgress(`Stage ${stageNumber} — ${readingsByStage[stageNumber]?.length ?? 0} rows...`);
    try {
      await calculateStage(generalKey, stageNumber, dr, confirmReset);
      setRecalcProgress('Refreshing plots...');
      const freshReadings = await getReadings(generalKey);
      setReadingsByStage(freshReadings);
      let freshAnalysis: AnalysisData;
      try { freshAnalysis = await getAnalysis(generalKey, stageNumber); }
      catch { freshAnalysis = { ...EMPTY_ANALYSIS, dilationRange: dr }; }
      setAnalysisByStage((prev) => ({ ...prev, [stageNumber]: freshAnalysis }));
      // The stored value now IS the draft — drop the draft so the control
      // reflects exactly what the stage was calculated with.
      setDilationDraft((prev) => {
        const next = { ...prev };
        delete next[stageNumber];
        return next;
      });
      onRecalculated?.();
      setSnackbar({
        open: true,
        message: `Stage ${stageNumber} recalculated with a ${dr} mm dilation window (other stages unchanged)`,
        severity: 'success',
      });
    } catch (err: any) {
      // Handle cascade reset (409) — test is approved, needs confirmation
      if (err?.response?.status === 409 && err?.response?.data?.error === 'TEST_REQUIRES_RESET') {
        const status = err.response.data.currentStatus;
        const isApproved = status === 'Approved' || status === 'Review Completed';
        setConfirmDlg({
          open: true,
          title: isApproved ? 'Warning: Test has been approved' : 'Warning: Test has been completed',
          message: isApproved
            ? 'Changing the dilation range will reset the test status to "In Progress" and clear all signoff fields. The test will need to go through the full review and approval process again.'
            : 'Changing the dilation range will reset the test status to "In Progress" and clear Tested By and Test Date.',
          confirmLabel: 'Yes, Reset and Recalculate',
          confirmColor: 'warning',
          onConfirm: () => doRecalculate(stageNumber, true),
        });
      } else {
        setSnackbar({ open: true, message: 'Recalculation failed', severity: 'error' });
      }
    } finally {
      setRecalculating(false);
      setRecalcProgress('');
    }
  }, [generalKey, canEditTestData, dilationFor, readingsByStage, onRecalculated]);

  useEffect(() => {
    if (!generalKey) { setLoading(false); return; }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getReadings(generalKey);
        if (cancelled) return;
        setReadingsByStage(data);
        setError(null);

        // Load analysis data for each stage that has readings
        const stageNums = Object.keys(data).map(Number).filter((n) => data[n]?.length > 0);
        const analysisResults: Record<number, AnalysisData> = {};
        await Promise.all(
          stageNums.map(async (stageNum) => {
            try {
              analysisResults[stageNum] = await getAnalysis(generalKey, stageNum);
            } catch {
              analysisResults[stageNum] = { ...EMPTY_ANALYSIS };
            }
          }),
        );
        if (!cancelled) setAnalysisByStage(analysisResults);
      } catch {
        if (!cancelled) setError('Failed to load readings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadDataRef.current = load;
    load();
    return () => { cancelled = true; };
  }, [generalKey]);

  const stageNumbers = Object.keys(readingsByStage)
    .map(Number)
    .filter((n) => readingsByStage[n]?.length > 0)
    .sort();

  const tabs = [
    { label: 'SUMMARY', stageNumber: 0 },
    ...stageNumbers.map((n) => ({ label: `Stage ${n}`, stageNumber: n })),
  ];

  const selectedTab = tabs[activeStageTab] ?? tabs[0];

  const handleRangesChange = useCallback((stageNumber: number, ranges: AnalysisRanges) => {
    setAnalysisByStage((prev) => ({
      ...prev,
      [stageNumber]: { ...(prev[stageNumber] ?? EMPTY_ANALYSIS), ...ranges },
    }));
  }, []);

  // "Calculate" (formerly "Highlight") computes & persists Peak/Residual for
  // ALL stages at once. Peak/Residual use exactly whatever is entered per stage
  // — a blank Peak range means "no peak" for that stage (BUG-2026-0199 #2).
  const handleCalculate = useCallback(async () => {
    if (!generalKey) return;
    const stageNums = Object.keys(readingsByStage).map(Number).filter((n) => readingsByStage[n]?.length > 0);
    if (stageNums.length === 0) return;
    setSaving(true);
    try {
      const results: Record<number, AnalysisData> = {};
      for (const sn of stageNums) {
        const current = analysisByStage[sn] ?? EMPTY_ANALYSIS;
        // Default "From" to 0 when only "To" is provided.
        const peakFrom = current.peakRangeTo != null ? (current.peakRangeFrom ?? 0) : current.peakRangeFrom;
        const residualFrom = current.residualRangeTo != null ? (current.residualRangeFrom ?? 0) : current.residualRangeFrom;
        results[sn] = await saveAnalysisRanges(generalKey, sn, {
          peakRangeFrom: peakFrom,
          peakRangeTo: current.peakRangeTo,
          residualRangeFrom: residualFrom,
          residualRangeTo: current.residualRangeTo,
        });
      }
      setAnalysisByStage((prev) => ({ ...prev, ...results }));
      setSnackbar({ open: true, message: `Peak/residual computed for ${stageNums.length} stage(s)`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to compute analysis ranges', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [generalKey, readingsByStage, analysisByStage]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1 }}>
      {/* Dilation range controls moved into PeakResidualInputs row per stage */}

      {/* Sub-tab bar — full-width grid matching main tab style */}
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${tabs.length}, 1fr)`, mb: 2 }}>
        {tabs.map((t, idx) => {
          const isActive = activeStageTab === idx;
          const isSummary = t.stageNumber === 0;
          const stageCount = t.stageNumber > 0 ? (readingsByStage[t.stageNumber]?.length ?? 0) : 0;
          return (
            <Box
              key={t.label}
              onClick={() => setActiveStageTab(idx)}
              sx={{
                height: 42,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                borderBottom: isActive
                  ? `3px solid ${theme.palette.primary.main}`
                  : '3px solid transparent',
                backgroundColor: isActive
                  ? theme.palette.background.paper
                  : isSummary
                    ? alpha(theme.palette.primary.main, 0.08)
                    : '#F0F0EA',
                color: isActive
                  ? theme.palette.text.primary
                  : isSummary
                    ? theme.palette.primary.dark
                    : theme.palette.text.secondary,
                fontWeight: isActive ? 700 : isSummary ? 700 : 400,
                borderLeft: isSummary ? `2px solid ${alpha(theme.palette.primary.main, 0.3)}` : undefined,
                '&:hover': {
                  backgroundColor: isActive
                    ? theme.palette.background.paper
                    : alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: isSummary ? '0.88rem' : '0.82rem',
                  fontWeight: isSummary ? 800 : 'inherit',
                  letterSpacing: isSummary ? '0.04em' : undefined,
                  textTransform: isSummary ? 'uppercase' : undefined,
                  color: isSummary && isActive
                    ? theme.palette.primary.main
                    : undefined,
                }}
              >
                {isSummary ? '\u2211 Summary' : t.label}
              </Typography>
              {stageCount > 0 && (
                <Chip
                  label={stageCount}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    backgroundColor: alpha(theme.palette.success.main, 0.15),
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {selectedTab.stageNumber === 0 ? (
        <SummaryAnalysis
          readingsByStage={readingsByStage}
          analysisByStage={analysisByStage}
          stages={stages}
        />
      ) : (
        <StageAnalysis
          readings={readingsByStage[selectedTab.stageNumber] ?? []}
          stageNumber={selectedTab.stageNumber}
          analysis={analysisByStage[selectedTab.stageNumber] ?? EMPTY_ANALYSIS}
          onRangesChange={(r) => handleRangesChange(selectedTab.stageNumber, r)}
          onCalculate={handleCalculate}
          saving={saving}
          dilationRange={dilationFor(selectedTab.stageNumber)}
          onDilationRangeChange={(v) => setDilationFor(selectedTab.stageNumber, v)}
          onApplyRecalculate={() => doRecalculate(selectedTab.stageNumber, false)}
          recalculating={recalculating}
          recalcProgress={recalcProgress}
          readOnly={!canEditTestData}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
    </Box>
  );
};
